import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';

function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/auth/reset-password&type=recovery`,
    });

    if (error) {
      setStatus('error');
      setErrorMsg(error.message);
      return;
    }

    setStatus('idle');
    setCodeSent(true);
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    const { error } = await supabase.auth.verifyOtp({
      email,
      token: code,
      type: 'recovery',
    });

    if (error) {
      setStatus('error');
      setErrorMsg('Code invalide ou expiré. Vérifiez et réessayez.');
      return;
    }

    navigate('/auth/reset-password', { replace: true });
  };

  const handleResend = async () => {
    setResending(true);
    setErrorMsg('');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/auth/reset-password&type=recovery`,
    });
    setResending(false);
    if (error) {
      setErrorMsg(error.message);
    } else {
      setErrorMsg('Un nouveau code a été envoyé par email.');
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-12 md:py-20">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img
              src="https://static.readdy.ai/image/4348d35aef10b02e64c1a80f787cca17/52f74c8d2b3c4f047b22c4e6b94b8b4b.jpeg"
              alt="WorldApeiron"
              className="h-14 w-14 mx-auto mb-4 object-contain"
            />
            <h1 className="font-heading font-bold text-2xl text-foreground-50">
              Mot de passe oublié
            </h1>
            <p className="text-sm text-foreground-400 mt-2">
              {codeSent
                ? 'Entrez le code reçu par email'
                : 'Entrez votre email pour réinitialiser votre mot de passe'}
            </p>
          </div>

          {!codeSent ? (
            <form onSubmit={handleSendCode} className="space-y-5">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground-400 mb-1.5">
                  Adresse email
                </label>
                <div className="flex items-center bg-background-100 border border-background-200/70 rounded-lg overflow-hidden focus-within:border-primary-400/50">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-foreground-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-2.5 pr-3 outline-none"
                  />
                </div>
              </div>

              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {status === 'loading' ? 'Envoi en cours...' : 'Envoyer le code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyCode} className="space-y-5">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-foreground-400 mb-1.5">
                  Code de réinitialisation
                </label>
                <input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                  placeholder="••••••"
                  className="w-full bg-background-100 border border-background-200/70 rounded-lg py-3 px-4 text-center text-2xl tracking-[0.5em] text-foreground-50 placeholder:text-foreground-500 focus:border-primary-400/50 outline-none"
                />
              </div>

              {status === 'error' && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-sm text-red-400">
                  {errorMsg}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading' || code.length !== 6}
                className="w-full py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {status === 'loading' ? 'Vérification...' : 'Vérifier le code'}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending}
                  className="text-sm text-primary-400 hover:text-primary-300 transition-colors disabled:opacity-60"
                >
                  {resending ? 'Envoi en cours...' : 'Renvoyer le code'}
                </button>
                <button
                  type="button"
                  onClick={() => setCodeSent(false)}
                  className="block text-sm text-foreground-400 hover:text-foreground-400 transition-colors mx-auto"
                >
                  Modifier l&apos;email
                </button>
              </div>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-foreground-400">
            <Link to="/auth/login" className="text-primary-400 hover:text-primary-300 font-medium transition-colors">
              Retour à la connexion
            </Link>
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ForgotPassword;